import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { useDocStore, WELCOME_TEXT } from "../../state/docStore";
import { Button } from "../common/Button";
import { Textarea } from "../common/Textarea";
import type { ChatMessage } from "../../lib/types";
import "./ChatPanel.css";

/** The coach's opening message, shown as a real bubble before any conversation. */
const WELCOME_MESSAGE: ChatMessage = {
  id: "welcome-0",
  role: "assistant",
  content: WELCOME_TEXT,
  streaming: false,
  createdAt: 0,
};

/** Render inline **bold** as <strong>; line breaks are handled by CSS pre-wrap. */
function renderRich(text: string) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => {
    const m = /^\*\*([^*]+)\*\*$/.exec(part);
    return m ? <strong key={i}>{m[1]}</strong> : <span key={i}>{part}</span>;
  });
}

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === "user";
  const showThinking =
    !isUser && message.streaming && message.content.length === 0;

  return (
    <div className={`msg ${isUser ? "msg--user" : "msg--assistant"}`}>
      <div className="msg__role">{isUser ? "You" : "Scoping Assistant"}</div>
      <div className="msg__bubble">
        {showThinking ? (
          <span className="chat__thinking" aria-label="Assistant is thinking">
            <span />
            <span />
            <span />
          </span>
        ) : (
          <>
            {isUser ? message.content : renderRich(message.content)}
            {message.streaming && <span className="msg__caret" aria-hidden="true" />}
          </>
        )}
      </div>
    </div>
  );
}

export function ChatPanel() {
  const { messages, streaming, error, sendMessage } = useDocStore();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to the latest content as it streams in.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const lastContent = messages[messages.length - 1]?.content;
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lastContent]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text || streaming) return;
    setDraft("");
    void sendMessage(text);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="chat">
      <div className="chat__header">
        <div className="chat__title">Scoping conversation</div>
        <div className="chat__subtitle">
          Tell me about your project — I'll help you focus it into one buildable month.
        </div>
      </div>

      <div className="chat__messages" ref={scrollRef}>
        {/* Always greet first: show the coach's intro until a real conversation starts. */}
        {(messages.length === 0 ? [WELCOME_MESSAGE] : messages).map((m) => (
          <MessageBubble key={m.id} message={m} />
        ))}
        {error && <div className="chat__error">{error}</div>}
      </div>

      <div className="composer">
        <div className="composer__row">
          <Textarea
            className="composer__textarea"
            autoGrow
            maxAutoGrow={200}
            rows={1}
            placeholder="Describe your project, or reply to the assistant…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={handleKeyDown}
            aria-label="Message the Scoping Assistant"
          />
          <Button
            variant="primary"
            className="composer__send"
            onClick={handleSend}
            disabled={!draft.trim() || streaming}
            loading={streaming}
          >
            Send
          </Button>
        </div>
        <div className="composer__hint">
          Enter to send · Shift+Enter for a new line
        </div>
      </div>
    </div>
  );
}
